import { inject, PLATFORM_ID } from '@angular/core';
import { isPlatformServer } from '@angular/common';
import {
  HttpEventType,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpParams,
  HttpRequest,
} from '@angular/common/http';
import { map } from 'rxjs/operators';
import DOMPurify from 'dompurify';

export const sanitizeRequestInterceptor: HttpInterceptorFn = (
  req: HttpRequest<any>,
  next: HttpHandlerFn
) => {
  const platformId = inject(PLATFORM_ID);

  // Don't sanitize req./resp. on server.
  if (isPlatformServer(platformId)) {
    return next(req);
  }

  const skipSanitization = req.params.get('skipSanitize') === 'true';

  if (skipSanitization) {
    const clonedReq = req.clone({ params: req.params.delete('skipSanitize') });
    return next(clonedReq);
  }

  let sanitizedBody = req.body && sanitizeBody(req.body);
  const sanitizedParams = sanitizeParams(req.params);
  const clonedReq = req.clone({
    params: sanitizedParams,
    body: sanitizedBody,
  });

  return next(clonedReq).pipe(
    map((event) => {
      // Sanitizing response.
      if (event.type === HttpEventType.Response && event.body) {
        return event.clone({
          body: sanitizeBody(event.body),
        });
      }

      return event;
    })
  );
};

function sanitizeBody(body: any): any {
  let sanitizedBody: any = {};

  if (typeof body === 'string') {
    return DOMPurify.sanitize(body);
  }

  if (Array.isArray(body)) {
    return body.map((item) => sanitizeBody(item));
  }

  if (typeof body === 'object' && body !== null) {
    for (let [key, value] of Object.entries(body)) {
      sanitizedBody[key] = sanitizeBody(value);
    }
  }

  return Object.keys(sanitizedBody).length ? sanitizedBody : body;
}

function sanitizeParams(params: HttpParams) {
  let sanitizedParams = new HttpParams();

  for (let key of params.keys()) {
    sanitizedParams = sanitizedParams.set(
      key,
      DOMPurify.sanitize(params.get(key))
    );
  }

  return sanitizedParams;
}
